import { getEcho } from '@ottd/api';
import { useEffect, useRef, useState } from 'react';

type Handlers = Record<string, (payload: unknown) => void>;

// Minimal shape of the pusher-js connection Echo wraps (laravel-echo internal).
interface PusherConnectionLike {
  state?: string;
  bind(event: string, callback: () => void): void;
  unbind(event: string, callback: () => void): void;
}
interface ConnectorWithPusher {
  pusher?: { connection?: PusherConnectionLike };
}

/**
 * Subscribes to a public Reverb channel and dispatches `broadcastAs` events to
 * handlers, cleaning up on unmount / channel change. Event keys are the raw
 * `broadcastAs` names (e.g. `game.event`); the leading `.` Echo needs is added
 * here. Handlers are read through a ref so changing them never resubscribes.
 *
 * Returns whether the socket is actually **connected** (tracks the live pusher
 * connection state, not merely whether Reverb is configured). Callers treat
 * `false` - unconfigured, still connecting, or dropped mid-session - as "fall
 * back to `/api/live/*` polling", so a lost socket transparently resumes polls.
 */
export function useEchoChannel(channelName: string | null, handlers: Handlers): boolean {
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  const echo = getEcho();
  const [connected, setConnected] = useState(false);

  // Track the live socket state so degradation reacts to real drops.
  useEffect(() => {
    if (echo === null) {
      setConnected(false);
      return;
    }
    const connector = echo.connector as unknown as ConnectorWithPusher;
    const connection = connector.pusher?.connection;
    if (!connection) {
      // Unknown connector internals - assume up so we don't poll needlessly.
      setConnected(true);
      return;
    }
    const sync = () => setConnected(connection.state === 'connected');
    sync();
    connection.bind('state_change', sync);
    return () => connection.unbind('state_change', sync);
  }, [echo]);

  // Subscribe to the channel's events.
  useEffect(() => {
    if (echo === null || channelName === null) return;

    const channel = echo.channel(channelName);
    for (const name of Object.keys(handlersRef.current)) {
      channel.listen(`.${name}`, (payload: unknown) => {
        handlersRef.current[name]?.(payload);
      });
    }

    return () => {
      echo.leave(channelName);
    };
    // Only (re)subscribe when the channel identity or Echo availability changes.
  }, [echo, channelName]);

  return connected;
}
